import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Menu as MenuIcon, X as CloseIcon, ArrowRight, ChevronRight } from 'lucide-react';
import LogoImg from '../../assets/AcoSignFavIcon.png';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'About Us', path: '/about' },
    { label: 'Products & Solutions', path: '/products' },
    { label: 'Blog', path: '/blog' },
    { label: 'Contact Us', path: '/contact' },
  ];

  const handleNavClick = (path) => {
    setMobileOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTalkToUs = () => {
    setMobileOpen(false);
    window.open('https://wa.me/919112614174', '_blank', 'noopener,noreferrer');
  };

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        ...(scrolled
          ? {
              backgroundColor: 'rgba(255, 255, 255, 0.92)',
              backdropFilter: 'blur(20px)',
              borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
              boxShadow: '0 4px 20px rgba(15, 23, 42, 0.04)',
            }
          : {
              backgroundColor: 'rgba(255, 255, 255, 0.75)',
              backdropFilter: 'blur(12px)',
              borderBottom: '1px solid rgba(15, 23, 42, 0.05)',
            }),
      }}
    >
      <Container
        maxWidth={false}
        sx={{
          px: { xs: 1.5, sm: 2.5, md: 3 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: { xs: 70, md: 80 },
          }}
        >
          {/* Brand Logo & Name */}
          <Link
            to="/"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 12 }}
          >
            <Box
              component="img"
              src={LogoImg}
              alt="Coalitionify Logo"
              sx={{
                width: 42,
                height: 42,
                borderRadius: '10px',
                objectFit: 'contain',
                boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
              }}
            />
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.1rem', md: '1.25rem' },
                  letterSpacing: '-0.02em',
                  color: '#0F172A',
                  lineHeight: 1.1,
                }}
              >
                Coalitionify
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#2563EB',
                  display: 'block',
                }}
              >
                Innovate Private Limited
              </Typography>
            </Box>
          </Link>

          {/* Desktop Navigation Links */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {navLinks.map((link) => {
  const isActive =
    link.path === '/'
      ? location.pathname === '/'
      : location.pathname.startsWith(link.path);
                return (
                  <Button
                    key={link.path}
                    component={Link}
                    to={link.path}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      fontSize: '0.925rem',
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? '#2563EB' : '#334155',
                      backgroundColor: isActive ? 'rgba(37, 99, 235, 0.06)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(37, 99, 235, 0.08)',
                        color: '#2563EB',
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                );
              })}
            </Box>
          )}

          {/* Desktop CTA */}
          {!isMobile && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleTalkToUs}
              endIcon={<ArrowRight size={16} />}
              sx={{
                borderRadius: '10px',
                px: 2.5,
                py: 1.1,
                fontWeight: 600,
                fontSize: '0.9rem',
                boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
              }}
            >
              Talk to Us
            </Button>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle navigation menu"
              sx={{ color: '#0F172A' }}
            >
              {mobileOpen ? <CloseIcon size={26} /> : <MenuIcon size={26} />}
            </IconButton>
          )}
        </Box>
      </Container>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: '85%',
            maxWidth: 360,
            p: 3,
            backgroundColor: '#FFFFFF',
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid rgba(15, 23, 42, 0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              component="img"
              src={LogoImg}
              alt="Coalitionify Logo"
              sx={{ width: 36, height: 36, borderRadius: '8px', objectFit: 'contain' }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.1 }}>
              Coalitionify
            </Typography>
          </Box>
          <IconButton onClick={() => setMobileOpen(false)}>
            <CloseIcon size={22} />
          </IconButton>
        </Box>

        <List sx={{ width: '100%', mb: 3 }}>
          {navLinks.map((link) => {
            const isActive =
  link.path === '/'
    ? location.pathname === '/'
    : location.pathname.startsWith(link.path);
            return (
              <ListItemButton
                key={link.path}
                onClick={() => handleNavClick(link.path)}
                sx={{
                  borderRadius: '10px',
                  mb: 1,
                  backgroundColor: isActive ? 'rgba(37, 99, 235, 0.08)' : 'transparent',
                  color: isActive ? '#2563EB' : '#0F172A',
                }}
              >
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 700 : 500,
                    fontSize: '1.05rem',
                  }}
                />
                <ChevronRight size={18} opacity={0.5} />
              </ListItemButton>
            );
          })}
        </List>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          onClick={handleTalkToUs}
          endIcon={<ArrowRight size={18} />}
          sx={{ py: 1.5, borderRadius: '10px', fontWeight: 600 }}
        >
          Talk to Us
        </Button>
      </Drawer>
    </Box>
  );
};

export default Navbar;
